<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import moment from 'moment';
import { useStore } from 'vuex';
import PageTabs from '@/components/Utils/PageTabs.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import UiProfileHeader from '@/components/ui/UiProfileHeader.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useConfirm } from '@/composables/useConfirm';
import { onPageRefresh } from '@/composables/usePageRefresh';
import { useToast } from '@/composables/useToast';
import { formatDateTime, titleCase } from '@/utils/format';

// A parent asks one of their child's teachers for a meeting, and sees and
// cancels their requests. The server takes the parent from the sign-in and
// only accepts the child's own teachers.
const props = defineProps({
  // Row from My Children: id, name, grade_level, picture.
  child: { type: Object, default: null },
  // 'list' opens on My requests (e.g. from a notification about a reply).
  initialTab: { type: String, default: 'new' },
});
const open = defineModel({ type: Boolean, default: false });

const store = useStore();
const toast = useToast();
const { confirm } = useConfirm();

const tab = shallowRef('new');
const teachers = shallowRef([]);
const requests = shallowRef([]);
const loadingList = shallowRef(false);
const submitting = shallowRef(false);
const formRef = ref(null);
const form = ref({});

const today = () => moment().format('YYYY-MM-DD');
const notPast = (v) => !v || v >= today() || 'Choose today or a later date.';
const schoolHours = (v) => !v || (v >= '07:00' && v <= '17:00') || 'Choose a time between 7:00 AM and 5:00 PM.';

const fields = computed(() => [
  {
    key: 'teacherID',
    label: 'Teacher',
    type: 'select',
    required: true,
    // "Ana Reyes — Class adviser · Math, English"
    items: teachers.value.map((t) => ({
      id: t.id,
      label: t.role ? `${titleCase(t.teacher_name)} — ${t.role}` : titleCase(t.teacher_name),
    })),
    itemTitle: 'label',
    itemValue: 'id',
    span: 2,
    placeholder: teachers.value.length ? 'Choose a teacher' : 'No teachers found for this class',
  },
  {
    key: 'purpose',
    label: 'What would you like to discuss?',
    type: 'textarea',
    required: true,
    span: 2,
    placeholder: "e.g. My child's progress in Math this quarter",
  },
  { key: 'date', label: 'Preferred date', type: 'date', required: true, min: today(), rules: [notPast] },
  { key: 'time', label: 'Preferred time', type: 'time', required: true, rules: [schoolHours] },
]);

const teacherName = (id) => titleCase(teachers.value.find((t) => Number(t.id) === Number(id))?.teacher_name ?? 'Teacher');
// The other person on a request: the teacher asked, or the Prefect of
// Discipline who asked the parent (not one of the child's teachers).
const fromSchool = (r) => r.initiatedBy === 'school';
const staffName = (r) => titleCase(r.staffName || teacherName(r.teacherID));
// A meeting request from the school that's waiting for the parent.
const needsReply = (r) => fromSchool(r) && (r.status ?? 'pending') === 'pending' && isUpcoming(r);
const isUpcoming = (r) => moment(r.schedule).isSameOrAfter(moment(), 'minute');
// The teacher's answer; a past request nobody answered reads as "No reply".
const statusOf = (r) => {
  if (needsReply(r)) return { text: 'Needs your reply', variant: 'amber' };
  if (r.status === 'cancelled') return { text: 'Cancelled', variant: 'red' };
  if (r.status === 'declined') return { text: 'Declined', variant: 'secondary' };
  if (r.status === 'accepted') return { text: isUpcoming(r) ? 'Confirmed' : 'Done', variant: isUpcoming(r) ? 'green' : 'outline' };
  return isUpcoming(r) ? { text: 'Waiting for reply', variant: 'amber' } : { text: 'No reply', variant: 'outline' };
};
const sortedRequests = computed(() =>
  [...requests.value].sort((a, b) => {
    const up = Number(isOpen(b)) - Number(isOpen(a));
    return up || moment(a.schedule) - moment(b.schedule);
  }),
);
const isOpen = (r) => isUpcoming(r) && !['declined', 'cancelled'].includes(r.status);
// Who called it off, for the cancelled note.
const cancelledText = (r) =>
  `Cancelled by ${Number(r.cancelledBy) === Number(store.state.user?.id) ? 'you' : staffName(r)}${
    r.cancelledAt ? ` on ${moment(r.cancelledAt).format('MMM D')}` : ''
  }`;
// My requests keeps every request, whatever happened to it; the count is
// all of them so it never drops when one is cancelled or declined.
const TABS = computed(() => [
  { id: 'new', name: 'New request' },
  { id: 'list', name: 'My requests', count: requests.value.length },
]);

// Filter within My requests by what happened to each request.
// --- Answering the school's meeting requests ---------------------------
const moving = shallowRef(null);
const moveForm = ref({ date: '', time: '' });
const moveFields = computed(() => [
  { key: 'date', label: 'New date', type: 'date', required: true, min: today(), rules: [notPast] },
  { key: 'time', label: 'New time', type: 'time', required: true, rules: [schoolHours] },
]);
function startMove(r) {
  moving.value = r.id;
  const when = moment(r.schedule);
  moveForm.value = { date: when.format('YYYY-MM-DD'), time: when.format('HH:mm') };
}
async function respond(r, action) {
  const body = { action };
  if (action === 'reschedule') {
    if (!moveForm.value.date || !moveForm.value.time) {
      toast.error('Choose the new date and time.');
      return;
    }
    body.schedule = `${moveForm.value.date} ${moveForm.value.time}:00`;
  }
  if (action === 'decline') {
    const ok = await confirm({
      title: 'Decline this meeting?',
      description: `${staffName(r)} will be told you can't come on ${formatDateTime(r.schedule)}. You can suggest another time instead.`,
      confirmText: 'Decline',
      destructive: true,
    });
    if (!ok) return;
  }
  try {
    assertOk(await apiRequest('patch', `/parent-records/appointments/${r.id}/respond`, body));
    toast.success(action === 'decline' ? 'Meeting declined.' : action === 'reschedule' ? 'New time sent.' : 'Meeting confirmed.');
    moving.value = null;
    loadRequests();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not send your reply.'));
  }
}

const statusKey = (r) => {
  if (needsReply(r)) return 'waiting';
  if (r.status === 'cancelled' || r.status === 'declined') return r.status;
  if (r.status === 'accepted') return isUpcoming(r) ? 'confirmed' : 'past';
  return isUpcoming(r) ? 'waiting' : 'past';
};
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'past', label: 'Past' },
  { id: 'declined', label: 'Declined' },
  { id: 'cancelled', label: 'Cancelled' },
];
const filter = shallowRef('all');
const filterCounts = computed(() => {
  const counts = { all: requests.value.length };
  requests.value.forEach((r) => (counts[statusKey(r)] = (counts[statusKey(r)] || 0) + 1));
  return counts;
});
const filteredRequests = computed(() =>
  filter.value === 'all' ? sortedRequests.value : sortedRequests.value.filter((r) => statusKey(r) === filter.value),
);

async function loadTeachers() {
  try {
    const rows = await apiRequest('get', `/parent-records/getMyChildTeachers/${props.child.id}`);
    teachers.value = Array.isArray(rows) ? rows : [];
  } catch {
    teachers.value = [];
  }
}
async function loadRequests() {
  loadingList.value = true;
  try {
    const rows = await apiRequest(
      'get',
      `/parent-records/getMyAppointment/${props.child.id}/${store.getters.getFilterSelected}`,
    );
    requests.value = Array.isArray(rows) ? rows : [];
  } catch {
    requests.value = [];
  } finally {
    loadingList.value = false;
  }
}

// A teacher's answer arriving (or its notification opened) while the
// dialog is open updates My requests straight away.
onPageRefresh(() => {
  if (open.value && props.child) loadRequests();
});

watch(open, (isOpen) => {
  if (!isOpen || !props.child) return;
  tab.value = props.initialTab === 'list' ? 'list' : 'new';
  filter.value = 'all';
  form.value = { teacherID: null, purpose: '', date: '', time: '' };
  loadTeachers();
  loadRequests();
});

async function submit() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  submitting.value = true;
  try {
    assertOk(
      await apiRequest('post', '/parent-records/createAppointment', {
        studentID: props.child.id,
        teacherID: form.value.teacherID,
        school_yearID: store.getters.getFilterSelected,
        purpose: form.value.purpose.trim(),
        schedule: `${form.value.date} ${form.value.time}:00`,
      }),
    );
    toast.success(`Request sent to ${teacherName(form.value.teacherID)}.`);
    form.value = { teacherID: null, purpose: '', date: '', time: '' };
    formRef.value?.resetValidation();
    await loadRequests();
    tab.value = 'list';
  } catch (e) {
    toast.error(errorMessage(e, 'Could not send the request.'));
  } finally {
    submitting.value = false;
  }
}

async function cancel(r) {
  const ok = await confirm({
    title: 'Cancel this appointment?',
    description: `${teacherName(r.teacherID)} on ${formatDateTime(r.schedule)}. The teacher will be told.`,
    confirmText: 'Cancel appointment',
    cancelText: 'Keep it',
    destructive: true,
  });
  if (!ok) return;
  try {
    assertOk(await apiRequest('delete', `/parent-records/cancelAppointment/${r.id}`));
    toast.success('Appointment cancelled.');
    loadRequests();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not cancel the appointment.'));
  }
}

const photo = computed(() => {
  const name = String(props.child?.picture ?? '');
  return name && !name.startsWith('default')
    ? `${process.env.VUE_APP_SERVER}/enroll-student/view/studentFile/${encodeURIComponent(name)}`
    : '';
});
</script>

<template>
  <UiDialog v-model="open" title="Request an appointment" size="lg">
    <template #header>
      <UiProfileHeader
        :name="child?.name || ''"
        :photo="photo"
        :subtitle="['Appointments', child?.grade_level].filter(Boolean).join(' · ')"
        :size="48"
      />
    </template>

    <PageTabs v-model="tab" :items="TABS" class="ap-tabs" />

    <v-form v-if="tab === 'new'" ref="formRef" class="form-grid" @submit.prevent="submit">
      <UiFormFields v-model:values="form" :fields="fields" />
      <p class="ap-hint span-2">
        The teacher gets a notification and will accept, decline, or suggest another time. You'll be notified of their reply.
      </p>
    </v-form>

    <template v-else>
      <div v-if="loadingList && !requests.length" class="ap-empty">Loading…</div>
      <UiEmptyState
        v-else-if="!requests.length"
        icon="mdi-calendar-account-outline"
        title="No requests yet"
        description="Requests you send to your child's teachers show here."
      />
      <template v-else>
      <div class="ap-filters" role="group" aria-label="Filter requests">
        <button
          v-for="f in FILTERS.filter((x) => x.id === 'all' || filterCounts[x.id])"
          :key="f.id"
          type="button"
          class="ap-filter"
          :class="{ 'ap-filter--on': filter === f.id }"
          :aria-pressed="filter === f.id"
          @click="filter = f.id"
        >
          {{ f.label }}
          <span class="ap-filter__count">{{ filterCounts[f.id] || 0 }}</span>
        </button>
      </div>
      <ul class="ap-list">
        <li v-for="r in filteredRequests" :key="r.id" class="ap-item" :class="{ 'ap-item--past': !isUpcoming(r) }">
          <div class="ap-item__date">
            <span class="ap-item__month">{{ moment(r.schedule).format('MMM') }}</span>
            <span class="ap-item__day">{{ moment(r.schedule).format('D') }}</span>
          </div>
          <div class="ap-item__body">
            <div class="ap-item__head">
              <span class="ap-item__teacher">{{ staffName(r) }}</span>
              <UiBadge v-if="fromSchool(r)" variant="violet">Prefect of Discipline</UiBadge>
              <UiBadge :variant="statusOf(r).variant">{{ statusOf(r).text }}</UiBadge>
            </div>
            <span class="ap-item__time">{{ moment(r.schedule).format('dddd, h:mm A') }}</span>
            <span v-if="r.requestedSchedule" class="ap-item__moved">
              <v-icon size="14">mdi-calendar-edit-outline</v-icon>
              {{ fromSchool(r) ? 'You moved this' : 'The teacher moved this' }} from {{ formatDateTime(r.requestedSchedule) }}
            </span>
            <p class="ap-item__purpose">{{ r.purpose }}</p>
            <span v-if="r.status === 'cancelled'" class="ap-item__moved">
              <v-icon size="14">mdi-calendar-remove-outline</v-icon>
              {{ cancelledText(r) }}
            </span>
            <p v-if="r.responseNote" class="ap-item__note">
              <strong>{{ fromSchool(r) ? 'You' : staffName(r) }}:</strong> {{ r.responseNote }}
            </p>

            <!-- The Prefect of Discipline asked for this meeting: reply here -->
            <div v-if="needsReply(r)" class="ap-reply">
              <template v-if="moving === r.id">
                <div class="form-grid">
                  <UiFormFields v-model:values="moveForm" :fields="moveFields" />
                </div>
                <div class="ap-reply__actions">
                  <v-btn size="small" variant="text" @click="moving = null">Back</v-btn>
                  <v-btn size="small" color="primary" @click="respond(r, 'reschedule')">Send new time</v-btn>
                </div>
              </template>
              <div v-else class="ap-reply__actions">
                <v-btn size="small" variant="text" @click="respond(r, 'decline')">Decline</v-btn>
                <v-btn size="small" variant="outlined" @click="startMove(r)">Change time</v-btn>
                <v-btn size="small" color="primary" @click="respond(r, 'accept')">Accept</v-btn>
              </div>
            </div>
          </div>
          <v-btn v-if="isOpen(r) && !needsReply(r)" size="small" variant="text" @click="cancel(r)">Cancel</v-btn>
        </li>
      </ul>
      </template>
    </template>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Close</v-btn>
      <v-btn v-if="tab === 'new'" color="primary" :loading="submitting" :disabled="!teachers.length" @click="submit">
        Send request
      </v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.ap-tabs {
  margin-bottom: 16px;
}
.ap-hint {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.ap-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.ap-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--card);
  color: var(--secondary-foreground);
  font-size: 13px;
  cursor: pointer;
}
.ap-filter--on {
  border-color: var(--foreground);
  color: var(--foreground);
  font-weight: 500;
}
.ap-filter:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring-halo);
}
.ap-filter__count {
  font-size: 12px;
  color: var(--muted-foreground);
}
.ap-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.ap-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.ap-item--past {
  opacity: 0.7;
}
.ap-item__date {
  display: flex;
  flex: 0 0 48px;
  flex-direction: column;
  align-items: center;
  padding: 6px 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--subtle);
  line-height: 1.1;
}
.ap-item__month {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.ap-item__day {
  font-size: 18px;
  font-weight: 600;
}
.ap-item__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.ap-item__head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ap-item__teacher {
  font-size: 14px;
  font-weight: 500;
}
.ap-item__time {
  font-size: 13px;
  color: var(--muted-foreground);
}
.ap-item__moved {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--muted-foreground);
}
.ap-item__note {
  margin: 6px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--subtle);
  font-size: 13px;
}
.ap-reply {
  margin-top: 8px;
  padding: 10px;
  border-radius: 8px;
  background: var(--subtle);
}
.ap-reply__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
}
.ap-item__purpose {
  margin: 4px 0 0;
  font-size: 14px;
  overflow-wrap: anywhere;
}
.ap-empty {
  padding: 32px 16px;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
</style>
